import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  Radio,
  Row,
} from "antd";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";

import PublishLink from "./PublishLink";
import { getUserInfo } from "../../utils/utils";
import {
  configType,
  standardFieldsOptions,
} from "../../constant/WebToLead";
import { useLeadFormActions } from "./hooks/useLeadFormActions";
import FeatureComingSoon from "../../common/FeatureComingSoon";
import { useLeadCustomFieldDefinitions } from "../../common/hooks/useLeadCustomFieldDefinitions";
import { getLeadCustomFieldIsActive } from "../../utils/leadCustomFields";

const CreateForm = ({ initialValues }) => {
  const { fields: leadCustomFieldDefinitions } = useLeadCustomFieldDefinitions();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const user = getUserInfo();

  const standardFields = standardFieldsOptions || [];
  const mandatoryStandardFields = standardFields.filter(f => f.isOptional === false).map(f => f.fieldName);
  
  const allCustomFields = leadCustomFieldDefinitions.filter(
    (field) => getLeadCustomFieldIsActive(field) !== false,
  );
  const customFieldNamesOptions = allCustomFields.map((field) => field.fieldName);

  const [isPublishOpen, setIsPublishOpen] = useState(false);
  const [isConfirm, setConfirm] = useState(false);
  const [formType, setFormType] = useState();
  
  const [checkedList, setCheckedList] = useState([]);
  const [checkedCustomList, setCheckedCustomList] = useState([]);

  const { handleCreate, handleUpdate, loading } = useLeadFormActions(
    (error) =>
      form.setFields([
        {
          name: "formName",
          errors: [error],
        },
      ]),
    () => setConfirm(true),
  );

  useEffect(() => {
    if (initialValues?.id) {
      const selected = (initialValues?.webToLeadConfigFields || []).map((f) =>
        typeof f === "object" ? f?.fieldName : f
      );
      const standardSelected = selected.filter((name) =>
        standardFields.some((f) => f.fieldName === name)
      );

      const customRaw =
        initialValues?.webToLeadConfigCustomFields ||
        initialValues?.customFields ||
        [];
      const customSelected = customRaw
        .map((f) => (typeof f === "object" ? f?.fieldName : f))
        .filter((name) => allCustomFields.some((f) => f.fieldName === name));

      form.setFieldsValue({
        ...initialValues,
        webToLeadConfigFields: standardSelected,
        webToLeadConfigCustomFields: customSelected,
      });
      setCheckedList(standardSelected);
      setCheckedCustomList(customSelected);
      setFormType(initialValues?.configType);
    } else {
      const defaultStandardSelected = mandatoryStandardFields;
      form.setFieldsValue({
        configType: configType.EMBEDDED_HTML,
        webToLeadConfigFields: defaultStandardSelected,
        webToLeadConfigCustomFields: [],
        assignTo: user?.firstName
          ? `${user?.firstName || ""} ${user?.lastName || ""}`.trim()
          : "",
      });
      setCheckedList(defaultStandardSelected);
      setCheckedCustomList([]);
    }
  }, [initialValues, leadCustomFieldDefinitions]);

  const checkAll = standardFields.length > 0 && checkedList.length === standardFields.length;
  const onCheckAllChange = (e) => {
    const list = e.target.checked ? standardFields.map(f => f.fieldName) : mandatoryStandardFields;
    setCheckedList(list);
    form.setFieldsValue({ webToLeadConfigFields: list });
  };

  const checkAllCustom = customFieldNamesOptions.length > 0 && checkedCustomList.length === customFieldNamesOptions.length;
  const onCheckAllCustomChange = (e) => {
    const list = e.target.checked ? customFieldNamesOptions : [];
    setCheckedCustomList(list);
    form.setFieldsValue({ webToLeadConfigCustomFields: list });
  };

  const onFinish = (values) => {
    const formatCustomFields = () => {
      return (values.webToLeadConfigCustomFields || [])
        ?.map((name) => {
          const found = allCustomFields.find((i) => i.fieldName === name);
          return found
            ? {
                ...found,
                fieldCategory: "custom",
                isSelected: true,
                pickValues: found?.pickValues?.map((v) => ({
                  value: typeof v === "object" ? (v?.value || v) : v,
                })),
              }
            : null;
        })
        .filter(Boolean);
    };

    const formatStandardFields = () => {
      return (values.webToLeadConfigFields || [])
        ?.map((name) => {
          const found = standardFieldsOptions?.find((i) => i.fieldName === name);
          return found
            ? {
                ...found,
                fieldCategory: "standard",
                isSelected: true,
              }
            : null;
        })
        .filter(Boolean);
    };

    const payload = {
      ...values,
      active: true,
      userId: user?.id,
      webToLeadConfigFields: formatStandardFields(),
      webToLeadConfigCustomFields: formatCustomFields(),
    };

    if (initialValues?.id) {
      handleUpdate({ ...payload, id: initialValues.id }, 1, true);
    } else {
      handleCreate({ ...payload, createdDate: new Date(), updatedDate: new Date() });
    }
  };

  return (
    <div className="tw-flex tw-justify-center web-to-lead tw-text-text-primary tw-font-inter">
      <div style={{ width: "100%" }}>
        <div className="tw-mb-6 tw-flex tw-items-center tw-gap-4">
          <div
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-xl tw-p-2 tw-bg-slate-100 hover:tw-bg-slate-200/60 tw-text-slate-600 tw-transition-colors"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={18} />
          </div>
          <div className="tw-flex tw-flex-col tw-justify-center tw-gap-0.5">
            <h1 className="tw-text-xl tw-font-bold tw-mb-0 tw-text-slate-800 tw-font-inter">
              {initialValues?.id ? "Edit Form Details" : "Create New Lead Form"}
            </h1>
            <p className="tw-text-xs tw-mb-0 tw-text-slate-500 tw-font-medium">
              {initialValues?.id
                ? "Modify form configurations, selected fields, and domain routing rules."
                : "Configure settings, select standard/custom fields, and generate HTML code for your form."}
            </p>
          </div>
        </div>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <div className="card mt20">
            <div className="heading">
              <Checkbox onChange={onCheckAllChange} checked={checkAll}>
                <div
                  className="tw-font-semibold tw-text-base tw-text-text-primary"
                  style={{ paddingLeft: 12 }}
                >
                  Select Fields For Lead Generation
                </div>
              </Checkbox>
            </div>
            <div className="content">
              <Form.Item name="webToLeadConfigFields" className="tw-mb-0">
                <Checkbox.Group className="w-100" onChange={setCheckedList}>
                  <Row gutter={[16, 16]} style={{ rowGap: 30 }}>
                    {standardFields.map((field, index) => {
                      const isMandatory = field.isOptional === false;
                      return (
                        <Col key={index} sm={8} lg={6}>
                          <Checkbox
                            value={field.fieldName}
                            disabled={isMandatory}
                            className={`${isMandatory ? "field-checked" : "field-item"} heading15px`}
                          >
                            <span style={{ paddingLeft: 12 }}>
                              {isMandatory && (
                                <span className="text-required">*</span>
                              )}
                              {field.fieldName}
                            </span>
                          </Checkbox>
                        </Col>
                      );
                    })}
                  </Row>
                </Checkbox.Group>
              </Form.Item>
            </div>
          </div>

          {customFieldNamesOptions.length > 0 && (
            <div className="card mt20">
              <div className="heading">
                <Checkbox onChange={onCheckAllCustomChange} checked={checkAllCustom}>
                  <div
                    className="tw-font-semibold tw-text-base tw-text-text-primary"
                    style={{ paddingLeft: 12 }}
                  >
                    Select Custom Fields For Lead Generation
                  </div>
                </Checkbox>
              </div>
              <div className="content">
                <Form.Item name="webToLeadConfigCustomFields" className="tw-mb-0">
                  <Checkbox.Group className="w-100" onChange={setCheckedCustomList}>
                    <Row gutter={[16, 16]} style={{ rowGap: 30 }}>
                      {allCustomFields.map((field, index) => {
                        const isMandatory = field.isOptional === false;
                        return (
                          <Col key={index} sm={8} lg={6}>
                            <Checkbox
                              value={field.fieldName}
                              className="heading15px"
                            >
                              <span style={{ paddingLeft: 12 }}>
                                {isMandatory && (
                                  <span className="text-required">*</span>
                                )}
                                {field.fieldName}
                              </span>
                            </Checkbox>
                          </Col>
                        );
                      })}
                    </Row>
                  </Checkbox.Group>
                </Form.Item>
              </div>
            </div>
          )}

          <Row
            gutter={[24, 24]}
            className="mt50 justify-content-between align-items-start"
          >
            <Col xs={24} lg={8}>
              <Form.Item
                label={<span className="tw-text-xs tw-font-bold tw-text-slate-600">Form Name</span>}
                name="formName"
                rules={[{ required: true, message: "Form Name is required" }]}
              >
                <Input className="tw-modal-input" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item
                label={<span className="tw-text-xs tw-font-bold tw-text-slate-600">Assign To</span>}
                name="assignTo"
                rules={[{ required: true, message: "Assign To is required" }]}
              >
                <Input disabled className="tw-modal-input" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item
                label={<span className="tw-text-xs tw-font-bold tw-text-slate-600">Your Domain Name</span>}
                name="domainName"
                rules={[
                  { required: true, message: "Please enter a domain name" },
                  {
                    pattern: /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}$/,
                    message: "Please enter a valid domain name (e.g., example.com)",
                  },
                ]}
              >
                <Input disabled={initialValues?.id} className="tw-modal-input" />
              </Form.Item>
            </Col>
          </Row>

          <div className="mt30 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center" style={{ gap: 20 }}>
            <div className="d-flex flex-column flex-md-row align-items-md-center" style={{ gap: 20, width: "100%" }}>
              <div className="heading15px">What do you want to create?</div>
              <Form.Item name="configType" className="tw-mb-0">
                <Radio.Group className="d-flex" disabled={initialValues?.id}>
                  <Radio value={configType.EMBEDDED_HTML}>HTML</Radio>
                  <FeatureComingSoon><Radio value={configType.SHARED_URL}>Form Link</Radio></FeatureComingSoon>
                  <Radio value={configType.EMBEDDED_SCRIPT}>Script</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
            <div className="d-flex justify-content-end align-items-center">
              <Button
                className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200 tw-mr-4"
                onClick={() => navigate("/settings/web-to-lead")}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                loading={loading}
                htmlType="submit"
                className="!tw-h-[38px] !tw-rounded-lg !tw-bg-theme !tw-border-theme !tw-text-white tw-text-xs tw-font-bold tw-px-5 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200"
              >
                {initialValues?.id ? "Update" : "Generate"}
              </Button>
              <PublishLink isPublish={isPublishOpen} handleCancel={() => setIsPublishOpen(false)} />
            </div>
          </div>
        </Form>
      </div>

      <Modal
        title={null}
        open={isConfirm}
        centered
        width={400}
        footer={null}
        onCancel={() => setConfirm(false)}
        className="delete-popup tw-overflow-hidden"
      >
        <div className="tw-bg-slate-50 tw-text-slate-800 tw-text-lg tw-font-bold tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border tw-border-solid tw-border-slate-100/50 tw-font-inter">
          Subscription expired
        </div>
        <div style={{ padding: "24px" }} className="tw-font-inter tw-text-sm tw-text-slate-600">
          Your subscription has expired, but the grace period is still active. Do
          you want to continue for up to 7 more days?
        </div>
        <div className="tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-3 tw-border tw-border-solid tw-border-slate-100/50">
          <Button
            className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
            onClick={() => setConfirm(false)}
          >
            No
          </Button>
          <Button
            type="primary"
            className="!tw-h-[38px] !tw-rounded-lg !tw-bg-theme !tw-border-theme !tw-text-white tw-text-xs tw-font-bold tw-px-5 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200"
            onClick={() => {
              onFinish({ ...form.getFieldsValue(), force: true });
              setConfirm(false);
            }}
          >
            Yes
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default CreateForm;
